export type CandidateDisplayStatus =
  | "Open to Connect"
  | "Applied"
  | "Shortlisted"
  | "Scheduled"
  | "Interviewed"
  | "Hired"
  | "Not Hired";

export type CandidateApplicantStatus =
  | "Applied"
  | "Shortlisted"
  | "Scheduled"
  | "Interviewed"
  | "Hired"
  | "Not Hired";

export interface CandidateStatusSource {
  application_status?: string | null;
  recruitment_status_name?: string | null;
  status?: string | null;
  current_stage?: string | null;
  applied_at?: string | null;
  apply_date?: string | null;
  applied_on?: string | null;
  shortlisted_at?: string | null;
  scheduled_at?: string | null;
  interviewed_at?: string | null;
  hired_at?: string | null;
  not_hired_at?: string | null;
  rejected_at?: string | null;
  shortlisted?: boolean | number;
  interview?: boolean | number;
  readyinterview?: boolean | number;
  hired?: boolean | number;
}

const normalizeStatusValue = (value?: string | null) =>
  String(value || "").trim().toLowerCase();

const hasAppliedMarker = (candidate?: CandidateStatusSource | null) =>
  Boolean(candidate?.applied_at || candidate?.apply_date || candidate?.applied_on);

const getDerivedPipelineStatus = (candidate?: CandidateStatusSource | null): CandidateDisplayStatus | null => {
  if (candidate?.hired_at || candidate?.hired) return "Hired";
  if (candidate?.not_hired_at || candidate?.rejected_at) return "Not Hired";
  if (candidate?.interviewed_at || candidate?.readyinterview) return "Interviewed";
  if (candidate?.scheduled_at || candidate?.interview) return "Scheduled";
  if (candidate?.shortlisted_at || candidate?.shortlisted) return "Shortlisted";
  return null;
};

export function normalizeCandidateDisplayStatus(
  value?: string | null,
  fallback: CandidateDisplayStatus = "Open to Connect",
): CandidateDisplayStatus {
  const normalized = normalizeStatusValue(value);

  if (normalized === "shortlist" || normalized === "shortlisted") return "Shortlisted";
  if (normalized === "scheduled" || normalized === "scheduled interview" || normalized === "schedule") return "Scheduled";
  if (normalized === "interviewed" || normalized === "interviewing") return "Interviewed";
  if (normalized === "hired") return "Hired";
  if (normalized === "not hired" || normalized === "rejected") return "Not Hired";
  if (normalized === "applied") return "Applied";
  if (normalized === "open to connect" || normalized === "open to work") return "Open to Connect";

  return fallback;
}

export function getCandidateDisplayStatus(
  candidate?: CandidateStatusSource | null,
  fallback: CandidateDisplayStatus = "Open to Connect",
): CandidateDisplayStatus {
  const normalized = normalizeStatusValue(
    candidate?.application_status ?? candidate?.recruitment_status_name ?? candidate?.status,
  );
  const derivedStatus = getDerivedPipelineStatus(candidate);

  if (derivedStatus) return derivedStatus;

  if (normalized) {
    if ((normalized === "open to connect" || normalized === "open to work") && hasAppliedMarker(candidate)) {
      return "Applied";
    }
    return normalizeCandidateDisplayStatus(normalized, fallback);
  }

  if (hasAppliedMarker(candidate)) return "Applied";

  return fallback;
}

export function getCandidateApplicantStatus(
  candidate?: CandidateStatusSource | null,
): CandidateApplicantStatus {
  const normalized = normalizeStatusValue(
    candidate?.application_status ?? candidate?.recruitment_status_name ?? candidate?.status,
  );
  const derivedStatus = getDerivedPipelineStatus(candidate);

  if (!normalized || normalized === "applied" || normalized === "open to connect" || normalized === "open to work") {
    if (derivedStatus) return derivedStatus as CandidateApplicantStatus;
    return "Applied";
  }

  if (normalized === "shortlist" || normalized === "shortlisted") return "Shortlisted";
  if (normalized === "scheduled" || normalized === "scheduled interview" || normalized === "schedule") return "Scheduled";
  if (normalized === "interviewed" || normalized === "interviewing") return "Interviewed";
  if (normalized === "hired") return "Hired";
  if (normalized === "not hired" || normalized === "rejected") return "Not Hired";

  return "Applied";
}
